import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useForm, getFormProps, getInputProps } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { SchemaSimulasi } from "~/scheme/schema_simulasi";
import { FetcherWithComponents, useFetcher } from "@remix-run/react";
import { useEffect, useState } from "react";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { TELE_BOT_TOKEN, TELE_CH_ID } from "~/assets/profile/profile";
import { GlobalAnimatedLayout } from "~/components/animation/GlobalAnimatedLayout";
import GlobalTextTitleH3 from "~/components/global/GlobalTextTitleH3";
import Separator from "~/components/global/Separator";

type FormErrors = {
  nama: string[];
  whatsapp: string[];
  lebar: string[];
  panjang: string[];
  bahan: string[];
};

export default function JasaBangunRumahDiBekasiSect6Simulasi() {
  const fetcher: FetcherWithComponents<any> = useFetcher();
  const [openDialog, setOpenDialog] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [hasilEstimasi, setHasilEstimasi] = useState<{
    total: number;
    luas: number;
    bahan: string;
    hargaPerMeter: number;
  } | null>(null);

  const [form, fields] = useForm({
    shouldValidate: "onInput", // ✅ validasi realtime saat input
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: SchemaSimulasi });
    },
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({
    nama: ["Nama wajib diisi"],
    whatsapp: [
      "Whatsapp digunakan untuk pengiriman hasil estimasi detail tabel perhitungan nantinya",
    ],
    lebar: ["Lebar tanah wajib diisi"],
    panjang: ["Panjang tanah wajib diisi"],
    bahan: ["Jenis bahan bangunan harus dipilih"],
  });

  const [selectedBahan, setSelectedBahan] = useState("");

  useEffect(() => {
    const isValid =
      fields.nama?.value?.trim() &&
      fields.whatsapp?.value?.trim() &&
      !fields.whatsapp?.errors?.length &&
      fields.lebar?.value &&
      fields.panjang?.value &&
      selectedBahan !== "";

    setIsFormValid(Boolean(isValid));
  }, [
    fields.nama?.value,
    fields.whatsapp?.value,
    fields.whatsapp?.errors,
    fields.lebar?.value,
    fields.panjang?.value,
    selectedBahan,
  ]);
  useEffect(() => {
    if (fetcher.data?.error) {
      alert(fetcher.data.error);
    }
  }, [fetcher.data?.error]);
  useEffect(() => {
    if (fetcher.data?.hasil) {
      setHasilEstimasi(fetcher.data.hasil);
      setOpenDialog(true);
      setFormErrors({
        nama: [],
        whatsapp: [],
        lebar: [],
        panjang: [],
        bahan: [],
      });
    }
  }, [fetcher.data]);
  const WHATSAPP_GREETING = "Halo, terima kasih sudah menghubungi kami!";
  // Fungsi kirim WA dan Telegram (disesuaikan isi message-nya)
  const handleSendToWhatsApp = (data: {
    nama: string;
    whatsapp: string;
    lebar: number;
    panjang: number;
    bahan: string;
  }) => {
    const now = new Date();
    const date = now.toLocaleDateString();
    const time = now.toLocaleTimeString();
    // const userWhatsAppLink = `https://wa.me/${
    //   data.whatsapp
    // }?text=${encodeURIComponent(
    //   `${WHATSAPP_GREETING}\nAda yang bisa saya bantu?\n☺️🙏`
    // )}`;

    let phone = data.whatsapp.trim();

    // Jika diawali "0", ubah menjadi "62"
    if (phone.startsWith("0")) {
      phone = "62" + phone.slice(1);
    }

    // Buat link WhatsApp
    const userWhatsAppLink = `https://wa.me/${phone}}`;

    const telegramMessage = `🟢 SIMULASI LANDING
Tanggal: ${date}
Jam: ${time}
Nama: ${data.nama}
Whatsapp: ${data.whatsapp}
Luas Tanah: ${data.lebar} x ${data.panjang} m
Bahan Bangunan: ${data.bahan}
Link Whatsapp User: ${userWhatsAppLink}`;
    console.log("📩 Sending to Telegram:", telegramMessage);
    const botToken = TELE_BOT_TOKEN;
    const chanId = TELE_CH_ID;
    const telegramApiUrl = `https://api.telegram.org/bot${botToken}/sendMessage`;

    fetch(telegramApiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        chat_id: chanId,
        text: telegramMessage,
      }),
    })
      .then((response) => response.json())
      .then((data) => console.log("✅ Message sent to Telegram:", data))
      .catch((error) => console.error("❌ Error sending to Telegram:", error));
  };

  // Ubah handleSubmit supaya setelah submit sukses, panggil handleSendToWhatsApp dengan data valid
  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const formElement = event.currentTarget.form;
    if (!formElement) return;

    const formData = new FormData(formElement);
    formData.set("bahan", selectedBahan);

    const data = {
      nama: formData.get("nama") as string,
      whatsapp: formData.get("whatsapp") as string,
      lebar: Number(formData.get("lebar")),
      panjang: Number(formData.get("panjang")),
      bahan: formData.get("bahan") as string,
    };

    const validation = SchemaSimulasi.safeParse(data);
    if (!validation.success) {
      const fieldErrors = validation.error.flatten().fieldErrors;
      setFormErrors({
        nama: fieldErrors.nama || [],
        whatsapp: fieldErrors.whatsapp || [],
        lebar: fieldErrors.lebar || [],
        panjang: fieldErrors.panjang || [],
        bahan: fieldErrors.bahan || [],
      });
      return;
    }

    formData.set("_action", "simulasi");
    fetcher.submit(formData, { method: "post" });

    // Panggil fungsi kirim WA & Telegram setelah validasi sukses
    handleSendToWhatsApp(data);
  };
  return (
    <>
      <Separator />
      <div className="w-global mx-auto flex flex-col lg:flex-row gap-5 lg:gap-20">
        {/* ============================
                      LEFT — STICKY TITLE
                  ============================ */}
        <div className="lg:w-[35%] w-full">
          <div className="lg:sticky lg:top-32 lg:pt-10">
            <GlobalAnimatedLayout direction="bottom" threshold={0.2}>
              <GlobalTextTitleH3
                center
                title="Cek Biayanya"
                desc=" Sebelum Anda berkonsultasi dengan kami, Anda bisa terlebih dulu
              menggunakan fitur kalkulator kami untuk menghitung estimasi biaya
              pembangunan rumah Anda secara sederhana."
              />
            </GlobalAnimatedLayout>
          </div>
        </div>

        {/* ============================
                      RIGHT — MAIN CONTENT
                  ============================ */}
        <div className=" lg:w-[65%] w-full flex flex-col gap-6">
          <fetcher.Form
            method="post"
            {...getFormProps(form)}
            className=" bg-yellow-500 p-[1rem] lg:p-[2rem] rounded-2xl space-y-4 shadow-lg"
          >
            <h3 className="text-xl font-semibold mb-3 text-center">
              Kalkulator Bangun Rumah
            </h3>
            <input type="hidden" name="_action" value="simulasi" />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  {...getInputProps(fields.lebar, { type: "number" })}
                  placeholder="Lebar tanah (m)"
                  onInput={() =>
                    setFormErrors((prev) => ({ ...prev, lebar: [] }))
                  }
                />
                {(fields.lebar?.errors?.[0] || formErrors.lebar[0]) && (
                  <p className="text-sm text-red-900 mt-1">
                    {fields.lebar?.errors?.[0] || formErrors.lebar[0]}
                  </p>
                )}
              </div>

              <div>
                <Input
                  {...getInputProps(fields.panjang, { type: "number" })}
                  placeholder="Panjang tanah (m)"
                  onInput={() =>
                    setFormErrors((prev) => ({ ...prev, panjang: [] }))
                  }
                />
                {(fields.panjang?.errors?.[0] || formErrors.panjang[0]) && (
                  <p className="text-sm text-red-900 mt-1">
                    {fields.panjang?.errors?.[0] || formErrors.panjang[0]}
                  </p>
                )}
              </div>
            </div>
            <div>
              <Select
                value={selectedBahan}
                onValueChange={(val) => {
                  setSelectedBahan(val);
                  setFormErrors((prev) => ({ ...prev, bahan: [] }));
                }}
                required
              >
                <input
                  type="hidden"
                  name={fields.bahan.name}
                  value={selectedBahan}
                />
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Pilih bahan bangunan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="murah">Murah</SelectItem>
                  <SelectItem value="menengah">Menengah</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
              {formErrors.bahan[0] && (
                <p className="text-sm text-red-900 mt-1 ml-1">
                  {formErrors.bahan[0]}
                </p>
              )}
            </div>
            <div>
              <Input
                {...getInputProps(fields.nama, { type: "text" })}
                placeholder="Nama Anda"
                onInput={() => setFormErrors((prev) => ({ ...prev, nama: [] }))}
              />
              {(fields.nama?.errors?.[0] || formErrors.nama[0]) && (
                <p className="text-sm text-red-900 mt-1 ml-1">
                  {fields.nama?.errors?.[0] || formErrors.nama[0]}
                </p>
              )}
            </div>
            <div>
              <Input
                {...getInputProps(fields.whatsapp, { type: "text" })}
                placeholder="contoh : 085123123123"
                onInput={() =>
                  setFormErrors((prev) => ({ ...prev, whatsapp: [] }))
                }
              />
              {(fields.whatsapp?.errors?.[0] || formErrors.whatsapp[0]) && (
                <p className="text-sm text-red-900 mt-1 ml-1">
                  {fields.whatsapp?.errors?.[0] || formErrors.whatsapp[0]}
                </p>
              )}
            </div>
            {!isFormValid && (
              <p className="text-sm text-red-900 font-semibold">
                Harap isi semua bidang untuk melanjutkan.
              </p>
            )}
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={!isFormValid}
              className="w-full bg-black text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Hitung Estimasi
            </Button>
            {fetcher.data?.error && (
              <p className="text-sm text-red-700 font-semibold">
                {fetcher.data.error}
              </p>
            )}
          </fetcher.Form>
        </div>
      </div>

      {/* <section className="bg-yellow-500  p-4 py-[5rem] my-[2rem] flex flex-col lg:flex-row items-center rounded-3xl">
        <h4 className="pb-[2rem] text-[30px] text-center font-black ">
          Mau Tau Biayanya Dulu? Coba ini
        </h4>
        <div className="w-full max-w-2xl mx-auto">
          
        </div>
      </section> */}

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Estimasi Biaya</DialogTitle>
          </DialogHeader>

          {hasilEstimasi && (
            <div>
              <p>Estimasi biaya pembangunan rumah Anda adalah:</p>
              <p className="text-lg font-bold text-green-700">
                Rp {hasilEstimasi.total.toLocaleString("id-ID")}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Luas Bangunan: {hasilEstimasi.luas} m² <br />
                Bahan: {hasilEstimasi.bahan} <br />
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
